import React, { useState, useCallback, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { authService } from "../../services/auth";
import { subscriptionService } from "../../services/subscription";
import { getApiErrorMessage } from "../../utils/apiErrors";

const FREE_PLAN_DESCRIPTION = "Basic insights into your audience, refreshed weekly";

const LIMIT_KEYS = [
  { key: "audiences", label: "Audiences" },
  { key: "brands", label: "Brands" },
  { key: "ideas", label: "Ideas this month" },
  { key: "scripts", label: "Scripts this month" },
  { key: "api_requests", label: "API requests (30d)" },
  { key: "projects", label: "Projects" },
];

function LimitProgressBar({ used, allowed, label }) {
  const safeAllowed = allowed > 0 ? allowed : 1;
  const pct = Math.min(100, (used / safeAllowed) * 100);
  const isOver = (allowed > 0 && used > allowed) || (allowed === 0 && used > 0);
  const isAtLimit = allowed > 0 && used === allowed && used > 0;

  let barColor = "bg-theme-primary opacity-70";
  if (isOver) barColor = "bg-red-500 opacity-80";
  else if (isAtLimit) barColor = "bg-amber-500 opacity-70";

  return (
    <div className="space-y-1">
      <div className="flex justify-between text-sm">
        <span className="text-theme-text opacity-70">{label}</span>
        <span className={`font-medium ${isOver ? "text-red-500 opacity-90" : "text-theme-text opacity-80"}`}>
          {used} / {allowed}
        </span>
      </div>
      <div className="h-1.5 w-full rounded-full bg-theme-secondary opacity-60 overflow-hidden">
        <div
          className={`h-full rounded-full transition-all duration-500 ${barColor}`}
          style={{ width: `${Math.min(100, pct)}%` }}
        />
      </div>
    </div>
  );
}

function formatExpiresAt(isoString) {
  if (!isoString) return "";
  const d = new Date(isoString);
  return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}

const primaryButtonClass =
  "px-5 py-2.5 bg-theme-primary text-theme-button rounded-lg text-sm font-medium hover:opacity-90 disabled:opacity-50 transition-opacity focus:outline-none focus:ring-2 focus:ring-theme-primary focus:ring-offset-2 focus:ring-offset-theme-main";

const secondaryButtonClass =
  "px-5 py-2.5 rounded-lg text-sm font-medium border border-theme-secondary text-theme-text hover:bg-theme-secondary hover:opacity-90 disabled:opacity-50 transition-opacity";

const Subscription = ({ currentUser }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [refreshedUser, setRefreshedUser] = useState(null);
  const [planConfig, setPlanConfig] = useState(null);
  const [scoutPlan, setScoutPlan] = useState(null);
  const [billingLoading, setBillingLoading] = useState(false);
  const [billingError, setBillingError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  const displayUser = refreshedUser || currentUser;
  const limits = displayUser?.limits || null;
  const timeTrial = displayUser?.time_trial || null;
  const currentSubscription = displayUser?.subscription || null;

  useEffect(() => {
    subscriptionService.getPlanConfig(
      (data) => {
        setPlanConfig(data.plans);
        setScoutPlan(data.scout);
      },
      () => setPlanConfig({})
    );
  }, []);

  const refreshUserConfig = useCallback((onFetched) => {
    authService.fetchUser(
      (response) => {
        if (response?.data) {
          setRefreshedUser(response.data);
          if (onFetched) onFetched(response.data);
        }
      },
      () => {}
    );
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const success = params.get("success");
    if (!success) return;

    if (success === "true") {
      setSuccessMessage("Your subscription is live!");
    } else if (success === "cancel") {
      setSuccessMessage("Your plan has been cancelled. You are now on the free tier.");
    } else if (success === "changed") {
      setSuccessMessage("Your plan has been updated.");
    }

    params.delete("success");
    navigate(
      {
        pathname: location.pathname,
        search: params.toString() ? `?${params.toString()}` : "",
      },
      { replace: true }
    );

    let attempts = 0;
    const pollUserConfig = () => {
      refreshUserConfig((data) => {
        if (success === "true" && data?.limits?.key === "scout" && attempts < 3) {
          attempts += 1;
          setTimeout(pollUserConfig, 2000);
        }
      });
    };

    setTimeout(pollUserConfig, 2000);
  }, [location.search, location.pathname, navigate, refreshUserConfig]);

  const planKey = limits?.key || "scout";
  const isFreePlan = planKey === "scout";
  const planLabel = isFreePlan ? "Free" : (planConfig?.[planKey]?.name || planKey);
  const freePlanDescription = scoutPlan?.description || FREE_PLAN_DESCRIPTION;
  const hasActiveSubscription = Boolean(currentSubscription?.active && !isFreePlan);

  const openBillingPortal = () => {
    setBillingError(null);
    setBillingLoading(true);
    subscriptionService.createBillingPortalSession(
      (data) => {
        if (data?.url) {
          window.location.href = data.url;
          return;
        }
        setBillingLoading(false);
        setBillingError("Could not open billing. Please try again.");
      },
      (requestError) => {
        setBillingLoading(false);
        setBillingError(getApiErrorMessage(requestError, "Could not open billing. Please try again."));
      }
    );
  };

  return (
    <div className="space-y-12">
      {successMessage && (
        <div className="rounded-lg border border-green-500 border-opacity-50 bg-green-600 bg-opacity-15 px-4 py-3 text-sm text-green-400">
          {successMessage}
        </div>
      )}

      <section>
        <h2 className="text-2xl font-semibold text-theme-text mb-4">Billing</h2>
        {limits ? (
          <div className="rounded-xl border border-theme-secondary bg-theme-secondary bg-opacity-40 overflow-hidden">
            <div className="p-6 space-y-3">
              <div>
                <p className="text-sm text-theme-text opacity-70">Current plan</p>
                <p className="text-2xl font-semibold text-theme-text mt-1">{planLabel}</p>
              </div>

              {hasActiveSubscription && currentSubscription?.activated_at && (
                <p className="text-sm text-theme-text opacity-70">
                  Activated {formatExpiresAt(currentSubscription.activated_at)}
                </p>
              )}
              {hasActiveSubscription && currentSubscription?.trial_expires_at && (
                <p className="text-sm text-theme-text opacity-70">
                  Trial ends {formatExpiresAt(currentSubscription.trial_expires_at)}
                </p>
              )}
              {hasActiveSubscription && currentSubscription?.next_bill_at && (
                <p className="text-sm text-theme-text opacity-70">
                  Next bill {formatExpiresAt(currentSubscription.next_bill_at)}
                </p>
              )}
              {isFreePlan && (
                <p className="text-sm text-theme-text opacity-75 max-w-2xl">
                  {freePlanDescription}
                </p>
              )}
              {timeTrial && (
                <p className="text-sm text-theme-text opacity-70">
                  Time trial expires {formatExpiresAt(timeTrial.expires_at)}
                </p>
              )}
              {billingError && (
                <div className="rounded-lg border border-theme-secondary bg-theme-main bg-opacity-40 px-4 py-3 text-sm text-theme-text">
                  {billingError}
                </div>
              )}
            </div>

            <div className="px-6 py-4 border-t border-theme-secondary flex flex-wrap items-center gap-3">
              {hasActiveSubscription ? (
                <>
                  <Link
                    to="/account/subscription/select/"
                    className={primaryButtonClass}
                    style={{ backgroundColor: "var(--theme-primary)" }}
                  >
                    Change plan
                  </Link>
                  <button
                    type="button"
                    onClick={openBillingPortal}
                    disabled={billingLoading}
                    className={secondaryButtonClass}
                  >
                    {billingLoading ? "Opening..." : "Manage billing"}
                  </button>
                  <Link
                    to="/account/subscription/cancel/"
                    className={secondaryButtonClass}
                  >
                    Cancel plan
                  </Link>
                </>
              ) : (
                <Link
                  to="/account/subscription/select/"
                  className={primaryButtonClass}
                  style={{ backgroundColor: "var(--theme-primary)" }}
                >
                  Upgrade
                </Link>
              )}
            </div>
          </div>
        ) : (
          <p className="text-theme-text opacity-80">Loading billing details...</p>
        )}
      </section>

      <section>
        <h2 className="text-2xl font-semibold text-theme-text mb-4">Limits</h2>
        {limits ? (
          <div className="space-y-4 p-5 rounded-xl border border-theme-secondary bg-theme-secondary bg-opacity-30 max-w-md">
            {LIMIT_KEYS.map(({ key, label }) => (
              <LimitProgressBar
                key={key}
                used={limits[`${key}_used`]}
                allowed={limits[`${key}_allowed`]}
                label={label}
              />
            ))}
          </div>
        ) : (
          <p className="text-theme-text opacity-80">Loading limits...</p>
        )}
      </section>
    </div>
  );
};

export default Subscription;
